export type StoreData<T> = {
  items: T[];
  loading: boolean;
};

export function createInitialData<T>(): StoreData<T> {
  return {
    items: [],
    loading: false,
  };
}

export type SearchSlice = {
  phrase: string;
};

export const initialSearchSlice: SearchSlice = {
  phrase: '',
};

export type DataActions<T> = {
  setItems: (items: T[]) => void;
  setLoading: (loading: boolean) => void;
  reset: () => void;
};

export type SearchActions = {
  setSearchPhrase: (phrase: string | ((prev: string) => string)) => void;
};

export type ZustandSet<T> = (
  partial: T | Partial<T> | ((state: T) => T | Partial<T>),
  replace?: false
) => void;

export function createDataActions<
  TItem,
  TStore extends { data: StoreData<TItem> },
>(set: ZustandSet<TStore>): DataActions<TItem> {
  return {
    setItems: (items) =>
      set((state) => ({
        ...state,
        data: { ...state.data, items, loading: false },
      })),
    setLoading: (loading) =>
      set((state) => ({
        ...state,
        data: { ...state.data, loading },
      })),
    reset: () =>
      set((state) => ({
        ...state,
        data: createInitialData<TItem>(),
      })),
  };
}

export function createSearchActions<TStore extends { search: SearchSlice }>(
  set: ZustandSet<TStore>
): SearchActions {
  return {
    setSearchPhrase: (value) =>
      set((state) => ({
        ...state,
        search: {
          ...state.search,
          phrase:
            typeof value === 'function' ? value(state.search.phrase) : value,
        },
      })),
  };
}

export type AsyncActionState = {
  loading: boolean;
  error: Error | null;
};

export const initialActionState: AsyncActionState = {
  loading: false,
  error: null,
};

export type AsyncAction<TArgs extends unknown[], TResult> = {
  execute: (...args: TArgs) => Promise<TResult>;
  loading: boolean;
  error: Error | null;
};

export type ActionsState<TActions extends string> = {
  [K in TActions]: AsyncActionState;
};

export function createInitialActionsState<T extends string>(
  actionNames: readonly T[]
): ActionsState<T> {
  const state = {} as ActionsState<T>;
  for (const name of actionNames) {
    state[name] = { ...initialActionState };
  }
  return state;
}

export function makeAction<
  TStore extends { _actionStates: ActionsState<string> },
  TArgs extends unknown[],
  TResult,
>(
  set: ZustandSet<TStore>,
  actionName: string,
  executor: (set: ZustandSet<TStore>, ...args: TArgs) => Promise<TResult>
): (...args: TArgs) => Promise<TResult> {
  return async (...args: TArgs): Promise<TResult> => {
    set((state) => ({
      ...state,
      _actionStates: {
        ...state._actionStates,
        [actionName]: { loading: true, error: null },
      },
    }));

    try {
      const result = await executor(set, ...args);
      set((state) => ({
        ...state,
        _actionStates: {
          ...state._actionStates,
          [actionName]: { loading: false, error: null },
        },
      }));
      return result;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      set((state) => ({
        ...state,
        _actionStates: {
          ...state._actionStates,
          [actionName]: { loading: false, error: err },
        },
      }));
      throw err;
    }
  };
}
